import React from 'react';
import { Table, Button, Space, Tag, message, Tooltip, Typography } from 'antd';
import { DownloadOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { TrackExportTask, TrackExportErrorDetail } from '../services/trackExportApi';
import {
  getInternalDownloadUrl,
  getExternalDownloadUrl,
} from '../services/trackExportApi';
import TablePaginationBar from '../../../../../components/TablePaginationBar';

const STATUS_PARSING = 0;
const STATUS_COMPLETED = 1;
const STATUS_VALIDATION_FAILED = 2;
const STATUS_FAILED = 3;

const STATUS_STR_TO_NUM: Record<string, number> = {
  PARSING: 0,
  COMPLETED: 1,
  VALIDATION_FAILED: 2,
  FAILED: 3,
};

/**
 * 获取状态标签
 * 映射：0→解析中、1→已完成、2→校验失败、3→执行失败
 */
function getStatusTag(status: number | string, t: (key: string) => string) {
  const numStatus =
    typeof status === 'string' ? STATUS_STR_TO_NUM[status] ?? -1 : status;
  const statusMap: Record<number, { color: string; text: string }> = {
    [STATUS_PARSING]: { color: 'processing', text: t('central.trackExport.status.parsing') },
    [STATUS_COMPLETED]: { color: 'success', text: t('central.trackExport.status.completed') },
    [STATUS_VALIDATION_FAILED]: {
      color: 'error',
      text: t('central.trackExport.status.validationFailed'),
    },
    [STATUS_FAILED]: { color: 'error', text: t('central.trackExport.status.failed') },
  };
  const statusInfo =
    statusMap[numStatus] ?? { color: 'default', text: t('central.trackExport.status.unknown') };
  return <Tag color={statusInfo.color}>{statusInfo.text}</Tag>;
}

interface TrackExportTaskTableProps {
  data: TrackExportTask[];
  loading: boolean;
  page: number;
  pageSize: number;
  totalElements: number;
  onPageChange: (page: number, pageSize: number) => void;
  onRefresh: () => void;
  onShowError: (detail: TrackExportErrorDetail) => void;
}

const TrackExportTaskTable: React.FC<TrackExportTaskTableProps> = ({
  data,
  loading,
  page,
  pageSize,
  totalElements,
  onPageChange,
  onRefresh,
  onShowError,
}) => {
  const { t } = useTranslation();
  const [downloading, setDownloading] = React.useState<Record<string, boolean>>({});

  const handleDownload = async (
    taskId: number,
    type: 'internal' | 'external',
    fileName: string
  ) => {
    const key = `${taskId}-${type}`;
    setDownloading((prev) => ({ ...prev, [key]: true }));
    try {
      const url =
        type === 'internal'
          ? await getInternalDownloadUrl(taskId)
          : await getExternalDownloadUrl(taskId);

      const a = document.createElement('a');
      a.href = url;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      message.success(t('central.trackExport.download.started'));
    } catch (e) {
      message.error(e instanceof Error ? e.message : t('central.trackExport.download.failed'));
    } finally {
      setDownloading((prev) => ({ ...prev, [key]: false }));
    }
  };

  const columns = [
    {
      title: t('central.trackExport.table.taskNo'),
      dataIndex: 'taskNo',
      key: 'taskNo',
      width: 160,
      ellipsis: { showTitle: false },
      render: (val: string) => (
        <Tooltip title={val}>
          <Typography.Text style={{ display: 'block' }} ellipsis>
            {val}
          </Typography.Text>
        </Tooltip>
      ),
    },
    {
      title: t('central.trackExport.table.fileName'),
      dataIndex: 'fileName',
      key: 'fileName',
      width: 180,
      ellipsis: { showTitle: false },
      render: (val: string) => (
        <Tooltip title={val}>
          <Typography.Text style={{ display: 'block' }} ellipsis>
            {val}
          </Typography.Text>
        </Tooltip>
      ),
    },
    {
      title: t('central.trackExport.table.operator'),
      dataIndex: 'operatorName',
      key: 'operatorName',
      width: 100,
      render: (val: string | null, row: TrackExportTask) => val ?? `ID: ${row.operatorId}`,
    },
    {
      title: t('central.trackExport.table.status'),
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: number | string) => getStatusTag(status, t),
    },
    {
      title: t('central.trackExport.table.createdAt'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      render: (val: string) => (val ? new Date(val).toLocaleString() : '-'),
    },
    {
      title: t('common.action'),
      key: 'action',
      width: 300,
      render: (_: unknown, row: TrackExportTask) => {
        const s = row.status;
        const canDownload = s === STATUS_COMPLETED;
        const isFailed = s === STATUS_VALIDATION_FAILED || s === STATUS_FAILED;

        let errorDetail: TrackExportErrorDetail | null = null;
        if (row.errorDetailJson) {
          try {
            errorDetail = JSON.parse(row.errorDetailJson) as TrackExportErrorDetail;
          } catch {
            errorDetail = { reason: row.errorDetailJson };
          }
        } else if (isFailed) {
          errorDetail = { reason: '-' };
        }

        const baseName = row.fileName.replace(/\.[^.]+$/, '');
        const internalName = `${baseName}_internal.xlsx`;
        const externalName = `${baseName}_external.xlsx`;

        const downloadDisabledTip = !canDownload
          ? t('central.trackExport.download.waitComplete')
          : undefined;

        const errorTooltipParts = [
          errorDetail?.reason,
          errorDetail?.message,
          errorDetail?.invalidRows?.length
            ? `${t('central.trackExport.error.invalidRows')}: ${errorDetail.invalidRows.join(', ')}`
            : '',
          errorDetail?.sample ? `${t('central.trackExport.error.sample')}: ${errorDetail.sample}` : '',
        ]
          .filter(Boolean)
          .join('\n');

        if (isFailed && errorDetail) {
          return (
            <Tooltip title={errorTooltipParts || t('central.trackExport.error.viewDetail')}>
              <Button
                type="link"
                size="small"
                danger
                icon={<ExclamationCircleOutlined />}
                onClick={() => onShowError(errorDetail!)}
              >
                {t('central.trackExport.error.viewDetail')}
              </Button>
            </Tooltip>
          );
        }

        return (
          <Space wrap>
            <Tooltip title={downloadDisabledTip}>
              <span>
                <Button
                  type="link"
                  size="small"
                  icon={<DownloadOutlined />}
                  loading={downloading[`${row.id}-internal`]}
                  disabled={!canDownload}
                  onClick={() => canDownload && handleDownload(row.id, 'internal', internalName)}
                >
                  {t('central.trackExport.download.internal')}
                </Button>
              </span>
            </Tooltip>
            <Tooltip title={downloadDisabledTip}>
              <span>
                <Button
                  type="link"
                  size="small"
                  icon={<DownloadOutlined />}
                  loading={downloading[`${row.id}-external`]}
                  disabled={!canDownload}
                  onClick={() => canDownload && handleDownload(row.id, 'external', externalName)}
                >
                  {t('central.trackExport.download.external')}
                </Button>
              </span>
            </Tooltip>
          </Space>
        );
      },
    },
  ];

  return (
    <div>
      <Table
        rowKey="id"
        columns={columns}
        dataSource={data}
        loading={loading}
        pagination={false}
        scroll={{ x: 1240 }}
        size="middle"
        tableLayout="fixed"
        className="mb-4"
        locale={{
          emptyText: (
            <div className="py-8 text-gray-400">
              <div className="text-base mb-1">{t('central.trackExport.table.empty')}</div>
              <div className="text-sm">{t('central.trackExport.table.emptyHint')}</div>
            </div>
          ),
        }}
      />
      <TablePaginationBar
        current={page}
        pageSize={pageSize}
        total={totalElements}
        onChange={onPageChange}
      />
    </div>
  );
};

export default TrackExportTaskTable;
